import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';

/**
 * 모바일 환경에서 키보드 상태 변화를 감지하고 관리하는 클래스
 */
export class KeyboardManager {
  private isKeyboardVisible = false;
  private lastWindowHeight = 0;
  private readonly heightThreshold: number = 0.8; // 키보드 감지를 위한 높이 임계값 (80%)

  // 이벤트 핸들러 참조 (cleanup을 위해 저장)
  private focusinHandler: ((e: Event) => void) | null = null;
  private focusoutHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;

  /**
   * KeyboardManager 생성자
   * @param onKeyboardShow 키보드가 표시될 때 실행할 콜백 함수 (현재 뷰포트 높이를 인자로 받음)
   * @param onKeyboardHide 키보드가 숨겨질 때 실행할 콜백 함수 (현재 뷰포트 높이를 인자로 받음)
   */
  constructor(
    private readonly onKeyboardShow?: (viewportHeight: number) => void,
    private readonly onKeyboardHide?: (viewportHeight: number) => void,
  ) {}

  /**
   * 키보드 상태 확인
   * @returns 키보드가 표시된 상태이면 true, 아니면 false
   */
  public isKeyboardActive(): boolean {
    return this.isKeyboardVisible;
  }

  /**
   * 현재 뷰포트 높이 반환
   * @returns 현재 뷰포트 높이 (픽셀)
   */
  public getViewportHeight(): number {
    return window.innerHeight;
  }

  /**
   * 키보드가 표시되기 전 원래 뷰포트 높이 반환
   * @returns 키보드가 표시되기 전 뷰포트 높이 (픽셀)
   */
  public getOriginalViewportHeight(): number {
    return this.lastWindowHeight;
  }

  /**
   * 키보드 높이 추정 (현재 뷰포트와 원래 뷰포트의 차이)
   * @returns 추정된 키보드 높이 (픽셀)
   */
  public getEstimatedKeyboardHeight(): number {
    if (!this.isKeyboardVisible) return 0;
    return Math.max(0, this.lastWindowHeight - window.innerHeight);
  }

  /**
   * 이벤트 리스너 설정 - 컴포넌트 마운트 시 호출
   * iOS와 Android 모두에서 키보드 상태 변화를 감지
   */
  public onMount(): void {
    // iOS 키보드 감지 (focusin/focusout 이벤트)
    this.lastWindowHeight = window.innerHeight;

    this.focusinHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        this.notifyKeyboardShow();
      }
    };

    this.focusoutHandler = () => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement)) {
        this.notifyKeyboardHide();
      }
    };

    // Android 키보드 감지 (resize 이벤트)
    this.resizeHandler = () => {
      const currentHeight = window.innerHeight;

      // 화면 높이가 크게 줄어든 경우 (키보드 표시)
      if (!this.isKeyboardVisible && currentHeight < this.lastWindowHeight * this.heightThreshold) {
        this.notifyKeyboardShow();
      }
      // 화면 높이가 원래대로 돌아온 경우 (키보드 숨김)
      else if (this.isKeyboardVisible && currentHeight > this.lastWindowHeight * 0.9) {
        this.notifyKeyboardHide();
      }

      // lastWindowHeight 값은 키보드가 표시되지 않은 상태의 높이를 유지
      if (!this.isKeyboardVisible || currentHeight > this.lastWindowHeight) {
        this.lastWindowHeight = currentHeight;
      }
    };

    if (MShopUtilF.isIOS()) {
      document.addEventListener('focusin', this.focusinHandler);
      document.addEventListener('focusout', this.focusoutHandler);
    }

    if (MShopUtilF.isAndroid()) {
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * 키보드 표시 상태로 변경하고 콜백 실행
   */
  private notifyKeyboardShow(): void {
    if (this.isKeyboardVisible) return;

    this.isKeyboardVisible = true;
    const currentViewportHeight = window.innerHeight;

    if (this.onKeyboardShow) {
      this.onKeyboardShow(currentViewportHeight);
    }
  }

  /**
   * 키보드 숨김 상태로 변경하고 콜백 실행
   */
  private notifyKeyboardHide(): void {
    if (!this.isKeyboardVisible) return;

    this.isKeyboardVisible = false;
    const currentViewportHeight = window.innerHeight;

    if (this.onKeyboardHide) {
      this.onKeyboardHide(currentViewportHeight);
    }
  }

  /**
   * 등록된 이벤트 리스너 제거 - 컴포넌트 언마운트 시 호출
   */
  public onDestroy(): void {
    // 등록된 이벤트 리스너 제거
    if (this.focusinHandler) {
      document.removeEventListener('focusin', this.focusinHandler);
      this.focusinHandler = null;
    }

    if (this.focusoutHandler) {
      document.removeEventListener('focusout', this.focusoutHandler);
      this.focusoutHandler = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }
}
